/**
 * Atelier — shared behavior for the Atelier section library.
 *
 * Progressive enhancement only: every section renders complete, usable
 * content without this file. Behaviors are keyed off data attributes,
 * are idempotent, and re-initialize on Shopify theme editor events.
 *
 * Behaviors:
 *   [data-atl-reveal]           scroll-reveal (opacity/transform only)
 *   [data-atl-reveal-stagger]   assigns stagger delays to revealed children
 *   [data-atl-count]            count-up number on first view
 *   [data-atl-countdown]        real-date countdown / daily shipping cutoff
 *   [data-atl-tabs]             accessible tabs
 *   [data-atl-ba]               before/after comparison slider
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var config = (window.Atelier && window.Atelier.config) || {};

  /* ------------------------------------------------------------------
     Scroll reveal
     ------------------------------------------------------------------ */
  var revealObserver = null;

  function initReveal(root) {
    if (config.reveal === false || reducedMotion.matches || !('IntersectionObserver' in window)) {
      return;
    }
    document.documentElement.classList.add('atl-reveal-ready');

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              entry.target.classList.add('atl-in');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0 }
      );
    }

    root.querySelectorAll('[data-atl-reveal-stagger]').forEach(function (group) {
      var i = 0;
      group.querySelectorAll('[data-atl-reveal]').forEach(function (el) {
        el.style.setProperty('--atl-reveal-delay', Math.min(i * 60, 360) + 'ms');
        i++;
      });
    });

    root.querySelectorAll('[data-atl-reveal]').forEach(function (el) {
      if (el.dataset.atlRevealInit) return;
      if (el.closest('[data-atl-no-reveal]')) return;
      el.dataset.atlRevealInit = 'true';
      // Elements already in the viewport on load appear instantly —
      // never make the visitor wait for above-the-fold content.
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        el.classList.add('atl-in', 'atl-instant');
      } else {
        revealObserver.observe(el);
      }
    });
  }

  /* ------------------------------------------------------------------
     Count-up numbers
     ------------------------------------------------------------------ */
  function animateCount(el) {
    var raw = el.getAttribute('data-atl-count') || el.textContent;
    var match = raw.match(/^([^0-9]*)([0-9][0-9,.]*)(.*)$/);
    if (!match) {
      el.textContent = raw;
      return;
    }
    var prefix = match[1];
    var suffix = match[3];
    var target = parseFloat(match[2].replace(/,/g, ''));
    var decimals = (match[2].split('.')[1] || '').length;
    var useComma = match[2].indexOf(',') !== -1;
    var start = null;
    var duration = 1100;

    function fmt(value) {
      var s = value.toFixed(decimals);
      if (useComma) s = s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return prefix + s + suffix;
    }

    function step(ts) {
      if (!start) start = ts;
      var p = Math.min((ts - start) / duration, 1);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(target * eased);
      if (p < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  }

  function initCounters(root) {
    var counters = root.querySelectorAll('[data-atl-count]');
    if (!counters.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      counters.forEach(function (el) {
        el.textContent = el.getAttribute('data-atl-count');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            animateCount(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.4 }
    );

    counters.forEach(function (el) {
      if (el.dataset.atlCountInit) return;
      el.dataset.atlCountInit = 'true';
      observer.observe(el);
    });
  }

  /* ------------------------------------------------------------------
     Countdown — honest timers only.
     Modes:
       data-deadline="2026-12-01T00:00:00-05:00"  fixed merchant date
       data-daily-cutoff="15:00"                  recurring dispatch cutoff
         + optional data-cutoff-days="1,2,3,4,5"  active weekdays (0 = Sun)
     End behavior (fixed deadline only): data-end="hide" | "message"
     ------------------------------------------------------------------ */
  function pad(n) {
    return String(n).padStart(2, '0');
  }

  function nextCutoff(timeStr, daysStr) {
    var parts = timeStr.split(':');
    var days = (daysStr || '0,1,2,3,4,5,6').split(',').map(Number);
    var candidate = new Date();
    candidate.setHours(parseInt(parts[0], 10) || 0, parseInt(parts[1], 10) || 0, 0, 0);
    for (var i = 0; i < 8; i++) {
      if (candidate.getTime() > Date.now() && days.indexOf(candidate.getDay()) !== -1) {
        return candidate;
      }
      candidate.setDate(candidate.getDate() + 1);
    }
    return null;
  }

  function initCountdowns(root) {
    root.querySelectorAll('[data-atl-countdown]').forEach(function (el) {
      if (el.dataset.atlCountdownInit) return;
      el.dataset.atlCountdownInit = 'true';

      var deadlineAttr = el.getAttribute('data-deadline');
      var cutoffAttr = el.getAttribute('data-daily-cutoff');
      var deadline = null;

      if (deadlineAttr) {
        deadline = new Date(deadlineAttr);
        if (isNaN(deadline.getTime())) deadline = null;
      } else if (cutoffAttr) {
        deadline = nextCutoff(cutoffAttr, el.getAttribute('data-cutoff-days'));
      }

      var wrap = el.closest('[data-atl-countdown-wrap]') || el;
      var done = wrap.querySelector('[data-atl-countdown-done]');
      var live = wrap.querySelector('[data-atl-countdown-live]');

      if (!deadline) {
        // No valid date configured: never show a fake timer. Hide on the
        // storefront, but keep it visible in the editor so it can be set up.
        if (!(window.Shopify && window.Shopify.designMode)) {
          wrap.setAttribute('hidden', '');
        }
        return;
      }

      var units = {
        days: el.querySelector('[data-unit="days"]'),
        hours: el.querySelector('[data-unit="hours"]'),
        minutes: el.querySelector('[data-unit="minutes"]'),
        seconds: el.querySelector('[data-unit="seconds"]'),
      };

      function finish() {
        clearInterval(timer);
        if (cutoffAttr) {
          // Recurring cutoff rolled over: recompute tomorrow's real cutoff.
          el.dataset.atlCountdownInit = '';
          initCountdowns(wrap.parentNode || document);
          return;
        }
        if (el.getAttribute('data-end') === 'message' && done) {
          if (live) live.setAttribute('hidden', '');
          done.removeAttribute('hidden');
        } else {
          wrap.setAttribute('hidden', '');
        }
      }

      function tick() {
        var diff = deadline.getTime() - Date.now();
        if (diff <= 0) {
          finish();
          return;
        }
        var s = Math.floor(diff / 1000);
        var d = Math.floor(s / 86400);
        var h = Math.floor((s % 86400) / 3600);
        var m = Math.floor((s % 3600) / 60);
        var sec = s % 60;

        if (units.days) {
          units.days.textContent = pad(d);
          var dayUnit = units.days.closest('.atl-countdown__unit');
          if (dayUnit && el.hasAttribute('data-hide-empty-days')) {
            dayUnit.style.display = d === 0 ? 'none' : '';
          }
        }
        if (units.hours) units.hours.textContent = pad(units.days ? h : h + d * 24);
        if (units.minutes) units.minutes.textContent = pad(m);
        if (units.seconds) units.seconds.textContent = pad(sec);
      }

      tick();
      var timer = setInterval(tick, 1000);
    });
  }

  /* ------------------------------------------------------------------
     Tabs (WAI-ARIA)
     ------------------------------------------------------------------ */
  function initTabs(root) {
    root.querySelectorAll('[data-atl-tabs]').forEach(function (widget) {
      if (widget.dataset.atlTabsInit) return;
      widget.dataset.atlTabsInit = 'true';

      var tabs = Array.prototype.slice.call(widget.querySelectorAll('[role="tab"]'));
      var panels = Array.prototype.slice.call(widget.querySelectorAll('[role="tabpanel"]'));

      function select(tab, focus) {
        tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', selected ? 'true' : 'false');
          t.tabIndex = selected ? 0 : -1;
        });
        panels.forEach(function (p) {
          p.hidden = p.id !== tab.getAttribute('aria-controls');
        });
        if (focus) tab.focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          select(tab, false);
        });
        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (next) {
            event.preventDefault();
            select(next, true);
          }
        });
      });
    });
  }

  /* ------------------------------------------------------------------
     Before / after slider
     ------------------------------------------------------------------ */
  function initBeforeAfter(root) {
    root.querySelectorAll('[data-atl-ba]').forEach(function (el) {
      if (el.dataset.atlBaInit) return;
      el.dataset.atlBaInit = 'true';

      var handle = el.querySelector('.atl-ba__handle');
      var position = 50;

      function setPosition(value) {
        position = Math.max(0, Math.min(100, value));
        el.style.setProperty('--atl-ba-pos', position + '%');
        if (handle) handle.setAttribute('aria-valuenow', String(Math.round(position)));
      }

      function positionFromEvent(event) {
        var rect = el.getBoundingClientRect();
        setPosition(((event.clientX - rect.left) / rect.width) * 100);
      }

      var dragging = false;

      el.addEventListener('pointerdown', function (event) {
        if (dragging) return;
        dragging = true;
        el.setPointerCapture(event.pointerId);
        positionFromEvent(event);
      });

      el.addEventListener('pointermove', function (event) {
        if (dragging) positionFromEvent(event);
      });

      ['pointerup', 'pointercancel'].forEach(function (type) {
        el.addEventListener(type, function () {
          dragging = false;
        });
      });

      if (handle) {
        handle.addEventListener('keydown', function (event) {
          var delta = 0;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') delta = -5;
          if (event.key === 'ArrowRight' || event.key === 'ArrowUp') delta = 5;
          if (event.key === 'Home') {
            setPosition(0);
            event.preventDefault();
            return;
          }
          if (event.key === 'End') {
            setPosition(100);
            event.preventDefault();
            return;
          }
          if (delta) {
            event.preventDefault();
            setPosition(position + delta);
          }
        });
      }

      setPosition(parseFloat(el.getAttribute('data-start') || '50'));
    });
  }

  /* ------------------------------------------------------------------
     Buy box gallery — scroll-snap track with thumbnail sync
     ------------------------------------------------------------------ */
  function initGalleries(root) {
    root.querySelectorAll('[data-atl-gallery]').forEach(function (gallery) {
      if (gallery.dataset.atlGalleryInit) return;
      gallery.dataset.atlGalleryInit = 'true';

      var track = gallery.querySelector('[data-atl-gallery-track]');
      var slides = gallery.querySelectorAll('[data-atl-gallery-slide]');
      var thumbs = gallery.querySelectorAll('[data-atl-gallery-thumb]');
      if (!track || slides.length < 2) return;

      function setActive(index) {
        thumbs.forEach(function (thumb) {
          thumb.classList.toggle('is-active', Number(thumb.dataset.atlGalleryThumb) === index);
        });
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var index = Number(thumb.dataset.atlGalleryThumb) || 0;
          var slide = slides[index];
          if (slide) track.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        });
      });

      var scrollTimer = null;
      track.addEventListener('scroll', function () {
        clearTimeout(scrollTimer);
        scrollTimer = setTimeout(function () {
          var index = Math.round(track.scrollLeft / track.clientWidth);
          setActive(Math.max(0, Math.min(slides.length - 1, index)));
        }, 80);
      }, { passive: true });
    });
  }

  /* ------------------------------------------------------------------
     Boot + theme editor support
     ------------------------------------------------------------------ */
  function initAll(root) {
    initReveal(root);
    initCounters(root);
    initCountdowns(root);
    initTabs(root);
    initBeforeAfter(root);
    initGalleries(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
    // Content injected by the editor should never sit hidden waiting
    // for a scroll that already happened.
    event.target.querySelectorAll('[data-atl-reveal]').forEach(function (el) {
      el.classList.add('atl-in');
    });
  });
})();
